import React, { createContext, useContext, useEffect, useState } from 'react'
import { useCallSocket, type UseCallSocketReturn } from '@/hooks/useCallSocket'
import { usePermission } from '@/hooks/usePermission'
import { callApi } from '@/api/callApi'

export interface CallContextValue extends UseCallSocketReturn {
  /**
   * 电话系统对当前用户是否启用。两道闸都过才是 true：
   * 1. 权限档：用户持有 call:read（superuser 直接放行）——没有它连 WS 握手
   *    都会被后端 403，悬浮球对该用户只能是永远空的摆设；
   * 2. 租户档：租户至少有一条 call routing config（= 配置过电话系统）。
   *    GET /calls/routing 与其余 call 端点同样只要求 call:read，可安全探测。
   * false 时 CallStatusIcon 不渲染，useCallSocket 整套停机（不连 WS、不初始化
   * Twilio、不发种子请求）。
   */
  callSystemEnabled: boolean
}

const CallContext = createContext<CallContextValue | null>(null)

export function CallProvider({ children }: { children: React.ReactNode }) {
  const { hasPermission } = usePermission()
  // permissions 异步落地：登录初期为空数组 → canReadCalls=false，随 user store
  // 就绪翻转为 true 并触发下面的租户探测。
  const canReadCalls = hasPermission('call:read')

  // null = 尚未探测/探测中（视为未启用，避免闪现）；true/false = 探测结论。
  const [tenantConfigured, setTenantConfigured] = useState<boolean | null>(null)

  useEffect(() => {
    if (!canReadCalls) return
    let cancelled = false
    callApi
      .getRoutingConfigDetails()
      .then((configs) => {
        if (!cancelled) setTenantConfigured(configs.length > 0)
      })
      .catch((err) => {
        if (cancelled) return
        const status = (err as { response?: { status?: number } })?.response?.status
        // 403 → 权限判断与后端不一致（保守关闭）；其余错误（网络抖动/5xx）
        // fail-open：不能因为一次瞬时失败就把真坐席的电话系统藏掉。
        setTenantConfigured(status === 403 ? false : true)
      })
    return () => {
      cancelled = true
    }
  }, [canReadCalls])

  const callSystemEnabled = canReadCalls && tenantConfigured === true
  // 不用 useMemo：callSocket 本身每次 render 都是新对象（hook 返回对象字面量），
  // memo 永不命中，写了反而误导。
  const callSocket = useCallSocket(callSystemEnabled)

  return (
    <CallContext.Provider value={{ ...callSocket, callSystemEnabled }}>
      {children}
    </CallContext.Provider>
  )
}

export function useCallContext(): CallContextValue {
  const context = useContext(CallContext)
  if (!context) {
    throw new Error('useCallContext must be used within CallProvider')
  }
  return context
}
